// Correct an already-issued quote's price where it stands (Session ARF).
//
// A quote the customer has already been sent can carry the wrong price (the case that prompted this: seven
// quotes issued without the multi-lift portfolio band). Grouping never reprices an issued quote, rightly, and
// the calculator's own manual price either returns an issued quote to draft or re-sends it. This panel does
// neither: it sets a manual price on one tier, previewed first, and the quote is repriced in place, so the
// customer's existing link shows the corrected figure next time they open it. NOTHING IS SENT; resending is
// the separate Resend action above, if the customer should be told.
//
// RULE 6: what the customer sees now and the calculated figure (and the portfolio band's figure, when it is
// better than the band the quote was issued at) are always shown beside the manual figure, so it is never
// set blind. The panel only renders for a quote the server says is eligible, or one carrying a manual price.
import { useCallback, useEffect, useState } from "react";
import { api, type QuoteOverrideTier, type QuotePriceOverrideView, type QuotePriceTierSet } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDate } from "../../lib/format";

const TIERS: QuoteOverrideTier[] = ["core", "care", "complete"];
const TIER_LABEL: Record<QuoteOverrideTier, string> = { core: "Core", care: "Care", complete: "Complete" };

function money(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? "-" : `£${Math.round(n).toLocaleString("en-GB")}`;
}

function TierRow({ label, tiers, testId }: { label: string; tiers: QuotePriceTierSet | null; testId: string }) {
  if (!tiers) return null;
  return (
    <tr data-testid={testId}>
      <th scope="row" className="py-1 pr-3 text-left font-normal text-ele-body">
        {label}
      </th>
      {TIERS.map((t) => (
        <td key={t} className="py-1 pr-3 text-ele-strong">
          {money(tiers[t]?.annual)}
        </td>
      ))}
    </tr>
  );
}

export function QuotePriceOverridePanel({ quoteId, onChanged }: { quoteId: string; onChanged?: () => void }) {
  const [view, setView] = useState<QuotePriceOverrideView | null>(null);
  const [editing, setEditing] = useState(false);
  const [tier, setTier] = useState<QuoteOverrideTier>("core");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [after, setAfter] = useState<QuotePriceTierSet | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const v = await api.quotes.priceOverride(quoteId);
      setView(v);
      setTier(v.override?.tier ?? v.recommendedTier);
    } catch {
      // A read failure must never break the Quote Hub.
      setView(null);
    }
  }, [quoteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const parsedValue = (): number | null => {
    const n = Number(value.replace(/[^0-9.]/g, ""));
    return value.trim() && Number.isFinite(n) && n >= 0 ? n : null;
  };

  const preview = useCallback(async () => {
    setError(null);
    setNotice(null);
    const n = parsedValue();
    if (n == null) {
      setError("Enter the lift maintenance figure, a number of pounds per year, ex VAT.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.quotes.previewPriceOverride(quoteId, { tier, value: n });
      setAfter(res.after);
    } catch (err) {
      setError(friendlyApiError(err, "Could not preview this price. Please try again."));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, tier, value]);

  const save = useCallback(async () => {
    setError(null);
    setNotice(null);
    const n = parsedValue();
    if (n == null) {
      setError("Enter the lift maintenance figure, a number of pounds per year, ex VAT.");
      return;
    }
    if (!reason.trim()) {
      setError("Enter a reason for the manual price. It is recorded for the office and never shown to the customer.");
      return;
    }
    setBusy(true);
    try {
      const v = await api.quotes.setPriceOverride(quoteId, { tier, value: n, reason: reason.trim() });
      setView(v);
      setEditing(false);
      setAfter(null);
      setValue("");
      setReason("");
      setNotice(`Price corrected. ${TIER_LABEL[tier]} is now ${money(v.current?.[tier]?.annual)} a year. Nothing was sent to the customer.`);
      onChanged?.();
    } catch (err) {
      setError(friendlyApiError(err, "Could not set the price. Please try again."));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, tier, value, reason, onChanged]);

  const clear = useCallback(async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const v = await api.quotes.clearPriceOverride(quoteId);
      setView(v);
      setNotice("Manual price removed. The quote is back on its calculated price. Nothing was sent to the customer.");
      onChanged?.();
    } catch (err) {
      setError(friendlyApiError(err, "Could not remove the manual price. Please try again."));
    } finally {
      setBusy(false);
    }
  }, [quoteId, onChanged]);

  if (!view || (!view.eligible && !view.override)) return null;

  const band = view.atCurrentPortfolioBand;
  const suggested = band?.tiers[tier]?.maintenanceAnnual ?? view.calculated?.[tier]?.maintenanceAnnual;

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4" aria-labelledby="quote-price-override-h">
      <h2 id="quote-price-override-h" className="font-heading text-lg tracking-wide text-ele-strong">
        Correct the price
      </h2>
      <p className="mt-2 text-sm text-ele-body">
        For a quote already sent at the wrong price. It sets a manual price on one tier and updates the quote where it
        stands, so the customer&apos;s existing link shows it next time they open it. Nothing is sent to the customer: use
        Resend if they should be told.
      </p>

      <table className="mt-3 text-sm">
        <thead>
          <tr>
            <th />
            {TIERS.map((t) => (
              <th key={t} scope="col" className="pr-3 text-left font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">
                {TIER_LABEL[t]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TierRow label="Customer sees now" tiers={view.current} testId="qpo-current" />
          <TierRow label="Calculated, no manual price" tiers={view.calculated} testId="qpo-calculated" />
          {band ? (
            <TierRow
              label={`At the portfolio band (${band.portfolioLiftCount} lifts, ${band.multiLiftDiscountPct}%)`}
              tiers={band.tiers}
              testId="qpo-band"
            />
          ) : null}
          {after ? <TierRow label="After this change" tiers={after} testId="qpo-after" /> : null}
        </tbody>
      </table>

      {view.override ? (
        <div className="mt-3 rounded-ele border border-ele-hairline p-3">
          <p className="text-sm text-ele-strong">
            Manual price on {TIER_LABEL[view.override.tier]}: <b>{money(view.override.value)}</b> a year lift maintenance.
          </p>
          <p className="mt-1 text-xs text-ele-body">
            Set {formatDate(view.override.setAt)}
            {view.override.setByName ? ` by ${view.override.setByName}` : ""}.{view.override.reason ? ` Reason: ${view.override.reason}` : ""}
          </p>
          {view.eligible ? (
            <ConfirmButton
              onConfirm={() => void clear()}
              busy={busy}
              busyLabel="Removing..."
              label="Remove manual price"
              confirmLabel="Press again to remove"
              className="mt-2 rounded-ele border border-ele-hairline px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90 disabled:opacity-50"
              armedClassName="font-semibold ring-2 ring-ele-blue"
            />
          ) : null}
        </div>
      ) : null}

      {notice ? (
        <p role="status" className="mt-2 text-sm text-ele-green">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      ) : null}

      {view.eligible ? (
        editing ? (
          <div className="mt-3 space-y-2">
            <div>
              <label className="block text-xs uppercase tracking-wide text-ele-body" htmlFor="qpo-tier">
                Tier
              </label>
              <select
                id="qpo-tier"
                value={tier}
                onChange={(e) => {
                  setTier(e.target.value as QuoteOverrideTier);
                  setAfter(null);
                }}
                className="mt-1 rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {TIER_LABEL[t]}
                    {t === view.recommendedTier ? " (recommended)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-ele-body" htmlFor="qpo-value">
                Lift maintenance a year, ex VAT (items on the quote add on top)
              </label>
              <input
                id="qpo-value"
                inputMode="decimal"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setAfter(null);
                }}
                placeholder={suggested != null ? String(Math.round(suggested)) : ""}
                className="mt-1 w-40 rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-ele-body" htmlFor="qpo-reason">
                Reason (required, for the office record, never shown to the customer)
              </label>
              <input
                id="qpo-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. portfolio band missed when the quote was sent"
                className="mt-1 w-full max-w-sm rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void preview()}
                className="max-sm:min-h-11 rounded-ele border border-ele-hairline px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90 disabled:opacity-50"
              >
                Preview
              </button>
              {after ? (
                <ConfirmButton
                  onConfirm={() => void save()}
                  busy={busy}
                  busyLabel="Saving..."
                  label="Set this price"
                  confirmLabel="Press again to set"
                  className="max-sm:min-h-11 rounded-ele bg-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-bg transition hover:opacity-90 disabled:opacity-50"
                  armedClassName="font-semibold ring-2 ring-ele-blue"
                />
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setAfter(null);
                  setError(null);
                }}
                className="max-sm:min-h-11 rounded-ele border border-ele-hairline px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-3 rounded-ele border border-ele-hairline px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90"
          >
            {view.override ? "Change the manual price" : "Set a manual price"}
          </button>
        )
      ) : null}
    </section>
  );
}
